import ENV from 'admin-panel/config/environment';

import type {
  IntegrationConfig,
  IntegrationStackEnv,
} from 'admin-panel/models/integrations/configuration';

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const merge = (defaults: unknown, value: unknown): unknown => {
  if (!isPlainObject(defaults)) {
    return value === undefined ? defaults : value;
  }

  if (value === undefined || value === null) {
    return defaults;
  }

  if (!isPlainObject(value)) {
    return value;
  }

  const merged: Record<string, unknown> = { ...value };

  for (const [key, fallback] of Object.entries(defaults)) {
    merged[key] = merge(fallback, value[key]);
  }

  return merged;
};

export const withDefaults = (
  defaults: IntegrationConfig,
  config?: IntegrationConfig
): IntegrationConfig =>
  structuredClone(merge(defaults, config)) as IntegrationConfig;

const HOSTNAME = /^[a-z\d]([a-z\d-]*[a-z\d])?(\.[a-z\d]([a-z\d-]*[a-z\d])?)*$/i;

export const isEndpointUrl = (value: string) => {
  if (/\s/.test(value) || value.endsWith('/')) {
    return false;
  }

  try {
    const { protocol, hostname } = new URL(value);

    return (
      (protocol === 'http:' || protocol === 'https:') && HOSTNAME.test(hostname)
    );
  } catch {
    return false;
  }
};

export const isTimeZone = (value: string) => {
  try {
    new Intl.DateTimeFormat(undefined, { timeZone: value });

    return true;
  } catch {
    return false;
  }
};

export const webhookUrl = ({
  service,
  integrationUuid,
  stackEnv,
  id = 'full',
}: {
  service: string;
  integrationUuid: string;
  stackEnv: IntegrationStackEnv;
  id?: 'full' | 'middle';
}) => {
  const webhookId =
    id === 'middle'
      ? integrationUuid.split('-').slice(1, -1).join('-')
      : integrationUuid;

  return `${ENV.it3.hosts[stackEnv]}/v2/webhooks/${service}/${webhookId}`;
};
